// Shared machinery for emulator-backed Munos hosts. The headless
// emulator client (@multinostalgia/server) and the browser emulator
// host (@multinostalgia/web) both drive a core through the same
// per-frame sequence — step the core, fire the script's frame event at
// OAM-DMA time, composite the script's draw_image calls onto the fresh
// framebuffer, mask overscan, deliver to sinks. Every piece of that
// logic that is not genuinely tier-specific lives here, so the
// fidelity rig compares two executions of ONE rendering path rather
// than two hand-synced copies of it.
import { allocImageDescriptor, arrData, arrLength, } from "./memory-utils.js";
import { MAX_IMAGE_PIXELS, MAX_IMAGE_STORE_BYTES, MunosResourceError, } from "../limits.js";
// Reject an image whose declared dimensions would allocate an unreasonable host
// buffer BEFORE that buffer is allocated. Traps via MunosResourceError so the
// event aborts deterministically on every tier, exactly like the heap cap
// (docs/trust-model.md). `image_from_bytes`/`_bitmap_bytes` carry their width
// and height in single wire bytes (0..255) so they can't reach here oversized;
// `image_literal` and `image_from_png_bytes` take raw i32 args and must be
// checked at their builtin boundary.
export function assertImageDims(w, h) {
    if (!Number.isInteger(w) || !Number.isInteger(h) ||
        w < 0 || h < 0 || w * h > MAX_IMAGE_PIXELS) {
        throw new MunosResourceError(`munos: image ${w}x${h} exceeds the ${MAX_IMAGE_PIXELS}-pixel per-image limit`);
    }
}
// Per-system overscan in pixels (top/bottom + left/right). NES games
// rendered into 256×240 but TVs cropped the outer 8px ring, so hosts
// mask those rows/cols to black after script-side draw_image runs.
// GB's LCD shows the full 160×144 — no mask needed.
export function overscanForSystem(system) {
    return system === "nes" ? 8 : 0;
}
// Mask the overscan ring to black. No-op when `overscan` is 0.
export function applyOverscanMask(fb, screenW, screenH, overscan) {
    if (overscan <= 0)
        return;
    // Top + bottom bands: clear whole rows.
    fb.fill(0, 0, overscan * screenW);
    fb.fill(0, (screenH - overscan) * screenW, screenH * screenW);
    // Left + right columns on the remaining middle rows.
    for (let y = overscan; y < screenH - overscan; y++) {
        const row = y * screenW;
        fb.fill(0, row, row + overscan);
        fb.fill(0, row + screenW - overscan, row + screenW);
    }
}
// Image descriptors keyed by the wasm pointer returned to the script.
// `alloc` is the standard create-descriptor-and-stash path; `adopt`
// registers pixels under a pointer some other bookkeeping already
// allocated (the headless client mirrors its ghost-image entries).
export class ImageStore {
    images = new Map();
    // Cumulative palette-index bytes held across every live image. Bounds the
    // per-instance host memory a script can tie up in images — there is no
    // per-image free today, so this only grows until clear() (script teardown).
    totalBytes = 0;
    charge(bytes) {
        if (this.totalBytes + bytes > MAX_IMAGE_STORE_BYTES) {
            throw new MunosResourceError(`munos: images exceed the ${MAX_IMAGE_STORE_BYTES}-byte per-instance ` +
                `image budget (holding ${this.totalBytes}, +${bytes})`);
        }
        this.totalBytes += bytes;
    }
    alloc(h, w, hgt, pixels, offsetX = 0, offsetY = 0) {
        // Per-image bound first (defence in depth behind the decode-boundary
        // check), then the cumulative budget — both before touching the heap.
        assertImageDims(w, hgt);
        this.charge(pixels.length);
        const ptr = allocImageDescriptor(h, w, hgt);
        this.images.set(ptr, { width: w, height: hgt, pixels, offsetX, offsetY });
        return ptr;
    }
    adopt(ptr, img) {
        this.charge(img.pixels.length);
        this.images.set(ptr, img);
    }
    get(ptr) {
        return this.images.get(ptr);
    }
    clear() {
        this.images.clear();
        this.totalBytes = 0;
    }
}
// Composite one frame's recorded draw_image calls onto a framebuffer.
//
// The Munos palette format is 0x00RRGGBB (R high, B low — what
// docs/language.md calls "lower 24 bits = packed RGB"). The
// framebuffer the cores write is 0x00BBGGRR (B high, R low — the
// canonical core-contract layout; non-native cores convert once in
// their adapter). We swap R↔B when compositing the script's draws so
// the framebuffer stays in one consistent layout; downstream consumers
// just OR 0xff000000 to get canvas-ready RGBA bytes.
// `bgMask` (optional) is the local console's per-pixel background-opacity
// mask for this frame (see BaseCore.readBackgroundMask): screenW*screenH
// bytes, 1 where an opaque non-backdrop background pixel sits. A draw with
// `behindBackground` set is suppressed at any pixel the mask marks opaque,
// reproducing OAM background-priority (a pipe-diving ghost sinks behind the
// pipe). Null/absent = no occlusion; every draw composites on top as before.
export function paintDrawCalls(h, fb, screenW, screenH, images, calls, bgMask = null) {
    for (const call of calls)
        paintOneDrawCall(h, fb, screenW, screenH, images, call, bgMask);
}
function paintOneDrawCall(h, fb, screenW, screenH, images, call, bgMask) {
    const img = images.get(call.img);
    if (!img) {
        // Could be a script bug, or could be that the script ran on a
        // non-rendering client before us. Either way, no pixels to draw.
        return;
    }
    // Palette is a Munos u32[]: 16-byte header (ptr/size/length/refcount)
    // + separate data block. See memory-utils.ts §Array layout.
    const palLen = arrLength(h, call.pal);
    const palBase = arrData(h, call.pal);
    const fullW = img.width;
    const fullH = img.height;
    const clipX = clamp(call.clipX, 0, fullW);
    const clipY = clamp(call.clipY, 0, fullH);
    const clipW = call.clipW < 0 ? (fullW - clipX) : clamp(call.clipW, 0, fullW - clipX);
    const clipH = call.clipH < 0 ? (fullH - clipY) : clamp(call.clipH, 0, fullH - clipY);
    // Apply the image's per-pose animation-alignment offset, negating
    // under flip so a single author-tuned value stays correct in both
    // facings. PNG-loaded images and untuned bitmaps carry zeros and
    // pass through unchanged.
    const offX = call.flipX ? -img.offsetX : img.offsetX;
    const offY = call.flipY ? -img.offsetY : img.offsetY;
    const baseX = call.x + offX;
    const baseY = call.y + offY;
    // Effective screen-space scissor. Each clipRect side defaults to -1
    // ("unbounded on this side"); fold that into the framebuffer edge
    // so the inner loop only does one comparison per side. When the
    // script passes no clipRect args these bounds are exactly the
    // framebuffer rect.
    const sx1 = call.clipRectX1 >= 0 ? Math.max(0, call.clipRectX1) : 0;
    const sy1 = call.clipRectY1 >= 0 ? Math.max(0, call.clipRectY1) : 0;
    const sx2 = call.clipRectX2 >= 0 ? Math.min(screenW, call.clipRectX2) : screenW;
    const sy2 = call.clipRectY2 >= 0 ? Math.min(screenH, call.clipRectY2) : screenH;
    // OAM background-priority: when the draw opts in and we have a background
    // mask, an overlay pixel is hidden wherever the local background is opaque
    // (mask == 1), exactly as the PPU hides a priority-bit sprite behind
    // non-backdrop background. No mask (non-NES host, or bg rendering off) →
    // the draw composites normally.
    const occlude = !!call.behindBackground && bgMask !== null;
    for (let dy = 0; dy < clipH; dy++) {
        const sy = call.flipY ? (clipH - 1 - dy) : dy;
        const py = baseY + dy;
        if (py < sy1 || py >= sy2)
            continue;
        for (let dx = 0; dx < clipW; dx++) {
            const sx = call.flipX ? (clipW - 1 - dx) : dx;
            const px = baseX + dx;
            if (px < sx1 || px >= sx2)
                continue;
            if (occlude && bgMask[py * screenW + px])
                continue; // behind opaque background
            const idx = img.pixels[(clipY + sy) * fullW + (clipX + sx)] ?? 0;
            if (idx === 0)
                continue; // transparent
            if (idx >= palLen)
                continue; // out-of-range palette entry → skip
            const rgb = h.view.getInt32(palBase + idx * 4, true);
            // Swap R↔B to convert Munos 0x00RRGGBB → core-native 0x00BBGGRR.
            const r = (rgb >> 16) & 0xff;
            const g = (rgb >> 8) & 0xff;
            const b = rgb & 0xff;
            fb[py * screenW + px] = (b << 16) | (g << 8) | r;
        }
    }
}
function clamp(x, lo, hi) {
    if (x < lo)
        return lo;
    if (x > hi)
        return hi;
    return x;
}
// Would blitting `call` light at least one framebuffer pixel? I.e. does
// some non-transparent image pixel land inside the effective screen-space
// scissor (the script's clipRect folded with the framebuffer rect)?
//
// This is the drawcall-side analog of the engine's `isOffScreenParked`
// (fidelity-identify.ts): the NES parks a sprite off-screen by writing its
// OAM Y to >= 0xEF and the rig drops those before identifying the peer's
// pose. A ghost drawcall the runtime would paint NOTHING for — fully past a
// screen edge, e.g. a sword-beam particle that flew off the bottom — is
// equally invisible to the player and must not count as a drawn ghost, or
// the pose/position analyzers compare visible-filtered engine sprites
// against an unfiltered ghost (an asymmetry that penalizes a ghost for
// faithfully continuing a particle's trajectory off-screen).
//
// The geometry MUST match paintOneDrawCall above exactly — same offset/flip,
// same scissor folding — so "would paint" here and "did paint" there never
// disagree. Palette range is intentionally ignored (an out-of-range index is
// treated as opaque): that only ever KEEPS a borderline draw, the safe
// direction, and it never fires for a fully-off-screen draw (the position
// gate rejects those before any palette question). `behindBackground`
// occlusion is ALSO intentionally ignored: it's a display-layer effect
// (sprite sinks behind level geometry), not off-screen parking, so a
// pipe-diving ghost stays a real pose the analyzer must still identify even
// when the background hides most of its pixels this frame.
export function drawCallPaintsVisiblePixel(img, call, screenW, screenH) {
    const fullW = img.width;
    const fullH = img.height;
    const clipX = clamp(call.clipX, 0, fullW);
    const clipY = clamp(call.clipY, 0, fullH);
    const clipW = call.clipW < 0 ? (fullW - clipX) : clamp(call.clipW, 0, fullW - clipX);
    const clipH = call.clipH < 0 ? (fullH - clipY) : clamp(call.clipH, 0, fullH - clipY);
    const offX = call.flipX ? -img.offsetX : img.offsetX;
    const offY = call.flipY ? -img.offsetY : img.offsetY;
    const baseX = call.x + offX;
    const baseY = call.y + offY;
    const sx1 = call.clipRectX1 >= 0 ? Math.max(0, call.clipRectX1) : 0;
    const sy1 = call.clipRectY1 >= 0 ? Math.max(0, call.clipRectY1) : 0;
    const sx2 = call.clipRectX2 >= 0 ? Math.min(screenW, call.clipRectX2) : screenW;
    const sy2 = call.clipRectY2 >= 0 ? Math.min(screenH, call.clipRectY2) : screenH;
    for (let dy = 0; dy < clipH; dy++) {
        const sy = call.flipY ? (clipH - 1 - dy) : dy;
        const py = baseY + dy;
        if (py < sy1 || py >= sy2)
            continue;
        for (let dx = 0; dx < clipW; dx++) {
            const sx = call.flipX ? (clipW - 1 - dx) : dx;
            const px = baseX + dx;
            if (px < sx1 || px >= sx2)
                continue;
            const idx = img.pixels[(clipY + sy) * fullW + (clipX + sx)] ?? 0;
            if (idx === 0)
                continue; // transparent
            return true; // a pixel the player would actually see
        }
    }
    return false;
}
// Decode an image_literal's payload — a base64 string of palette
// indices — zero-padding short input to w*h. `error` is non-null when
// the base64 was malformed (the pixels are then fully zero-filled);
// the caller logs it with its own label prefix.
export function decodeImageLiteral(b64, w, h) {
    // Bound the declared size before allocating anything — otherwise a bogus
    // w*h (or the error path below) allocates the giant buffer regardless of
    // whether the base64 is valid.
    assertImageDims(w, h);
    let pixels;
    try {
        const bin = atob(b64);
        pixels = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++)
            pixels[i] = bin.charCodeAt(i) & 0xff;
    }
    catch (e) {
        return { pixels: new Uint8Array(w * h), error: String(e) };
    }
    if (pixels.length < w * h) {
        const padded = new Uint8Array(w * h);
        padded.set(pixels);
        pixels = padded;
    }
    return { pixels, error: null };
}
// Decode an image_from_bytes payload. Wire format: first byte width,
// second byte height, remaining bytes pixel indices in row-major order
// (zero-padded if short).
export function decodeImageWireBytes(bytes) {
    const w = bytes[0] ?? 0;
    const h = bytes[1] ?? 0;
    const need = w * h;
    const have = Math.max(0, bytes.length - 2);
    const pixels = new Uint8Array(need);
    pixels.set(bytes.subarray(2, 2 + Math.min(need, have)));
    return { width: w, height: h, pixels };
}
// The frame-event protocol shared by every emulator host:
//
//   1. `beginTick()` at the start of each frameTick — arms the gate.
//   2. The core's OAM-DMA callback calls `fireAtDma(fire)`. The first
//      DMA of the tick dispatches event_client_frame at the moment the
//      game commits its rendered representation — reads in the handler
//      see at-DMA RAM, draw_image records calls (the actual framebuffer
//      paint happens later, after core.frame() returns), sends carry
//      at-DMA-derived values. Redundant fires from mid-frame split
//      DMAs are suppressed. See docs/fidelity-rig-agent-guide.md.
//   3. After core.frame() returns (or was skipped because the ROM is
//      paused), `fireFallback(fire)` dispatches the event if no DMA
//      fired this tick (ROM paused, or the game skipped its DMA on a
//      lag frame) so the script still gets its per-frame call.
export class FrameEventGate {
    pending = false;
    beginTick() {
        this.pending = true;
    }
    fireAtDma(fire) {
        if (!this.pending)
            return;
        this.pending = false;
        fire();
    }
    fireFallback(fire) {
        if (!this.pending)
            return;
        this.pending = false;
        fire();
    }
}
// Snapshot of the framebuffer at the moment the script called
// `pause_rom()`. While paused, the host restores it into the working
// framebuffer before every `event frame` runs so the script's
// draw_image overlays always start from the frozen pre-pause game
// state instead of compounding on top of the previous frame's overlay
// (the lobby's dim-the-ROM panel would otherwise darken to solid
// black over a few seconds).
export class PausedBackdrop {
    snapshot = null;
    capture(fb) {
        this.snapshot = new Uint32Array(fb);
    }
    clear() {
        this.snapshot = null;
    }
    restore(fb) {
        if (this.snapshot)
            fb.set(this.snapshot);
    }
}
// Compose `bytes` little-endian bytes read through `readByte` into one
// (optionally sign-extended) value — the body every onMemRead shares.
// NES and Game Boy are both little-endian.
export function composeMemRead(readByte, addr, bytes, signed) {
    let v = 0;
    for (let i = 0; i < bytes; i++) {
        v |= (readByte(addr + i) & 0xff) << (i * 8);
    }
    if (signed) {
        const shift = 32 - bytes * 8;
        v = (v << shift) >> shift;
    }
    else {
        v = v >>> 0;
    }
    return v | 0;
}
// Spread `value` across `bytes` little-endian byte writes — the body
// every onMemWrite shares.
export function spreadMemWrite(writeByte, addr, value, bytes) {
    for (let i = 0; i < bytes; i++) {
        writeByte(addr + i, (value >> (i * 8)) & 0xff);
    }
}
//# sourceMappingURL=emulator-kit.js.map